import type { CSSProperties } from 'react';
import { pxToRem } from '@/theme/spacing';
import { FontWeight, getFontFamily } from '@/theme/typography';
import {
  TABLE_BODY_FONT_SIZE_PX,
  TABLE_BODY_PADDING,
  TABLE_HEADER_FONT_SIZE_PX,
  TABLE_HEADER_PADDING,
  TABLE_HEADER_TEXT_COLOR,
  TABLE_SIZE_VARIANT,
  TABLE_STYLE_VALUE,
} from './Cmarkdowntable.constants';
import type { CmarkdowntableProps } from './Cmarkdowntable.types';

type TableSizeVariant = typeof TABLE_SIZE_VARIANT[keyof typeof TABLE_SIZE_VARIANT];

export interface CmarkdowntableBodyLayout {
  horizontalScrollableStyle: CSSProperties;
  verticalScrollableStyle: CSSProperties;
  headerPadding: string;
  bodyPadding: string;
  headerCellContentStyle: CSSProperties;
  baseBodyCellContentStyle: CSSProperties;
  baseBodyWeight: FontWeight;
}

export function resolveCmarkdowntableBodyLayout(input: {
  isExpanded: boolean;
  textSize: CmarkdowntableProps['textSize'];
  textWeight: CmarkdowntableProps['textWeight'];
}): CmarkdowntableBodyLayout {
  const sizeVariant: TableSizeVariant = input.isExpanded
    ? TABLE_SIZE_VARIANT.Expanded
    : TABLE_SIZE_VARIANT.Compact;
  const bodyFontSizePx = input.textSize ?? TABLE_BODY_FONT_SIZE_PX[sizeVariant];
  const baseBodyWeight = input.textWeight ?? FontWeight.Medium;

  return {
    horizontalScrollableStyle: {
      width: TABLE_STYLE_VALUE.WidthFull,
      minWidth: 0,
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      touchAction: TABLE_STYLE_VALUE.TouchActionAuto,
    },
    verticalScrollableStyle: {
      width: TABLE_STYLE_VALUE.WidthFull,
      flex: 1,
      minHeight: 0,
    },
    headerPadding: TABLE_HEADER_PADDING[sizeVariant],
    bodyPadding: TABLE_BODY_PADDING[sizeVariant],
    headerCellContentStyle: {
      fontFamily: getFontFamily(),
      fontSize: TABLE_HEADER_FONT_SIZE_PX[sizeVariant],
      fontWeight: FontWeight.Bold,
      lineHeight: 1.4,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: TABLE_HEADER_TEXT_COLOR,
      whiteSpace: TABLE_STYLE_VALUE.WhiteSpaceNormal,
      overflowWrap: TABLE_STYLE_VALUE.OverflowWrapBreakWord,
    },
    baseBodyCellContentStyle: {
      fontFamily: getFontFamily(),
      fontSize: pxToRem(bodyFontSizePx),
      fontWeight: baseBodyWeight,
      lineHeight: 1.5,
      whiteSpace: TABLE_STYLE_VALUE.WhiteSpaceNormal,
      overflowWrap: TABLE_STYLE_VALUE.OverflowWrapBreakWord,
    },
    baseBodyWeight,
  };
}
